// ONNX-runtime-web wrapper. Mirrors the contract of detection.py:Detector —
// load model lazily, run inference on a frame, return Detection[].
//
// The model output for yolo11n is [1, 84, 8400]:
//   - 84 channels per anchor: [cx, cy, w, h, score_0, ..., score_79]
//   - 8400 anchors covering the 640x640 input grid at multiple scales
// We decode + NMS here so callers receive clean Detection objects.

// Full onnxruntime-web bundle — includes the WebGPU + WASM execution providers.
// The bundled WASM blob is ~24.7 MiB (just under Cloudflare Pages' 25 MiB
// per-file cap), so this still deploys cleanly. WebGPU is what makes inference
// feel live on machines that have it (Apple Silicon, modern Android, iOS 18+).
import * as ort from "onnxruntime-web";

import { COCO_CLASSES, type Detection, type DevicePreference } from "./types";

const INPUT_SIZE = 640;
const NUM_CLASSES = 80;
const NUM_BOX_COORDS = 4;

export interface DetectorOptions {
  modelUrl: string; // e.g. "/models/yolo11n.onnx"
  device?: DevicePreference;
  confidenceThreshold?: number;
  iouThreshold?: number;
  classes?: string[] | null; // optional whitelist; null = all
}

export interface DetectorStats {
  preprocessMs: number;
  inferenceMs: number;
  postprocessMs: number;
}

/** Letterbox parameters used to map model coords back to source image coords. */
interface LetterboxInfo {
  scale: number;
  padX: number;
  padY: number;
  srcW: number;
  srcH: number;
}

export class Detector {
  readonly modelUrl: string;
  readonly device: DevicePreference;
  confidenceThreshold: number;
  iouThreshold: number;
  classes: string[] | null;
  private session: ort.InferenceSession | null = null;
  private inputName: string = "images";
  private outputName: string = "output0";
  private classIdsFilter: number[] | null = null;
  private offscreen: OffscreenCanvas | null = null;
  // Hoisted to instance fields to avoid 4.9 MB allocation per frame
  // (source of severe GC pressure on iOS Safari).
  private chwBuffer: Float32Array | null = null;

  constructor(opts: DetectorOptions) {
    this.modelUrl = opts.modelUrl;
    this.device = opts.device ?? "wasm";
    this.confidenceThreshold = opts.confidenceThreshold ?? 0.5;
    this.iouThreshold = opts.iouThreshold ?? 0.45;
    this.classes = opts.classes ?? null;
  }

  /** Load the ONNX model. Idempotent; safe to call multiple times. */
  async load(): Promise<void> {
    if (this.session) return;

    // Limit thread count — 1-2 is plenty on phones, more starves the UI.
    ort.env.wasm.numThreads = Math.min(navigator.hardwareConcurrency ?? 1, 2);

    // Prefer the requested EP; fall back to WASM so things still work where
    // WebGPU is missing (older Safari, browsers without WebGPU enabled).
    const executionProviders =
      this.device === "webgpu" ? (["webgpu", "wasm"] as const) : (["wasm"] as const);
    this.session = await ort.InferenceSession.create(this.modelUrl, {
      executionProviders: [...executionProviders],
      graphOptimizationLevel: "all",
    });
    this.inputName = this.session.inputNames[0] ?? "images";
    this.outputName = this.session.outputNames[0] ?? "output0";
  }

  setClasses(classes: string[] | null): void {
    this.classes = classes;
    this.classIdsFilter = null; // invalidate cached resolution
  }

  /** The model's class id -> name mapping. Static for COCO-trained YOLO. */
  classIdMap(): Map<number, string> {
    return new Map(COCO_CLASSES.map((name, id) => [id, name] as const));
  }

  private resolvedClassIds(): number[] | null {
    if (this.classes === null) return null;
    if (this.classIdsFilter !== null) return this.classIdsFilter;
    const wanted = new Set(this.classes.map((n) => n.toLowerCase()));
    const ids: number[] = [];
    const seen = new Set<string>();
    COCO_CLASSES.forEach((name, id) => {
      if (wanted.has(name.toLowerCase())) {
        ids.push(id);
        seen.add(name.toLowerCase());
      }
    });
    const unknown = [...wanted].filter((w) => !seen.has(w));
    if (unknown.length > 0) {
      throw new Error(
        `Unknown class names ${JSON.stringify(unknown.sort())}. ` +
          `Available: ${COCO_CLASSES.slice().sort().join(", ")}`,
      );
    }
    this.classIdsFilter = ids;
    return ids;
  }

  /**
   * Run detection on a frame. Accepts anything ImageBitmap/HTMLVideoElement-like
   * that can be drawn to a canvas.
   */
  async detect(
    source: ImageBitmap | HTMLVideoElement | HTMLImageElement | OffscreenCanvas,
    srcW: number,
    srcH: number,
  ): Promise<{ detections: Detection[]; stats: DetectorStats }> {
    if (!this.session) throw new Error("Detector not loaded; await detector.load()");
    const t0 = performance.now();
    const { tensor, letterbox } = this.preprocess(source, srcW, srcH);
    const t1 = performance.now();
    const feeds: Record<string, ort.Tensor> = { [this.inputName]: tensor };
    const results = await this.session.run(feeds);
    const t2 = performance.now();
    const out = results[this.outputName]!;
    const detections = this.postprocess(out, letterbox);
    const t3 = performance.now();
    return {
      detections,
      stats: {
        preprocessMs: t1 - t0,
        inferenceMs: t2 - t1,
        postprocessMs: t3 - t2,
      },
    };
  }

  // --- internal -------------------------------------------------------------

  private preprocess(
    source: ImageBitmap | HTMLVideoElement | HTMLImageElement | OffscreenCanvas,
    srcW: number,
    srcH: number,
  ): { tensor: ort.Tensor; letterbox: LetterboxInfo } {
    if (!this.offscreen) this.offscreen = new OffscreenCanvas(INPUT_SIZE, INPUT_SIZE);
    const ctx = this.offscreen.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Could not get 2D context");

    // Letterbox: scale source so the longer edge fits in INPUT_SIZE, pad the rest.
    const scale = Math.min(INPUT_SIZE / srcW, INPUT_SIZE / srcH);
    const drawW = Math.round(srcW * scale);
    const drawH = Math.round(srcH * scale);
    const padX = Math.floor((INPUT_SIZE - drawW) / 2);
    const padY = Math.floor((INPUT_SIZE - drawH) / 2);

    ctx.fillStyle = "rgb(114,114,114)"; // standard YOLO letterbox grey
    ctx.fillRect(0, 0, INPUT_SIZE, INPUT_SIZE);
    ctx.drawImage(source as CanvasImageSource, padX, padY, drawW, drawH);

    const imageData = ctx.getImageData(0, 0, INPUT_SIZE, INPUT_SIZE);
    const { data } = imageData; // RGBA, uint8, length = 640*640*4

    // YOLO expects RGB CHW float32 normalized to [0,1]. Buffer is allocated
    // once per detector and mutated in place each frame.
    if (!this.chwBuffer) {
      this.chwBuffer = new Float32Array(3 * INPUT_SIZE * INPUT_SIZE);
    }
    const chw = this.chwBuffer;
    const planeSize = INPUT_SIZE * INPUT_SIZE;
    for (let i = 0, p = 0; p < planeSize; p += 1, i += 4) {
      chw[p] = data[i]! / 255;
      chw[planeSize + p] = data[i + 1]! / 255;
      chw[2 * planeSize + p] = data[i + 2]! / 255;
    }

    // The Tensor wrapper itself is small (just a reference); we recreate it
    // each call so ORT/WebGPU sees a fresh handle and re-uploads the buffer.
    const tensor = new ort.Tensor("float32", chw, [1, 3, INPUT_SIZE, INPUT_SIZE]);
    return {
      tensor,
      letterbox: { scale, padX, padY, srcW, srcH },
    };
  }

  private postprocess(out: ort.Tensor, lb: LetterboxInfo): Detection[] {
    // out.dims is [1, 84, 8400]; data is Float32Array of length 1*84*8400
    const dims = out.dims as number[];
    const data = out.data as Float32Array;
    const numChannels = dims[1] ?? NUM_BOX_COORDS + NUM_CLASSES;
    const numAnchors = dims[2] ?? 8400;

    const filterIds = this.resolvedClassIds();
    const filterSet = filterIds === null ? null : new Set(filterIds);

    type Cand = { x1: number; y1: number; x2: number; y2: number; score: number; clsId: number };
    const cands: Cand[] = [];

    // Output is laid out channel-first: data[c * numAnchors + a]
    for (let a = 0; a < numAnchors; a++) {
      // Find the best class score for this anchor.
      let bestScore = 0;
      let bestCls = -1;
      for (let c = 0; c < NUM_CLASSES; c++) {
        const s = data[(NUM_BOX_COORDS + c) * numAnchors + a]!;
        if (s > bestScore) {
          bestScore = s;
          bestCls = c;
        }
      }
      if (bestScore < this.confidenceThreshold) continue;
      if (filterSet !== null && !filterSet.has(bestCls)) continue;
      const cx = data[0 * numAnchors + a]!;
      const cy = data[1 * numAnchors + a]!;
      const w = data[2 * numAnchors + a]!;
      const h = data[3 * numAnchors + a]!;
      cands.push({
        x1: cx - w / 2,
        y1: cy - h / 2,
        x2: cx + w / 2,
        y2: cy + h / 2,
        score: bestScore,
        clsId: bestCls,
      });
      // Suppress unused-channel warning
      void numChannels;
    }

    const kept = nms(cands, this.iouThreshold);

    // Map letterboxed coords back to source image coords.
    return kept.map((c) => ({
      cls_id: c.clsId,
      cls_name: COCO_CLASSES[c.clsId] ?? String(c.clsId),
      confidence: c.score,
      bbox: {
        x1: clamp((c.x1 - lb.padX) / lb.scale, 0, lb.srcW),
        y1: clamp((c.y1 - lb.padY) / lb.scale, 0, lb.srcH),
        x2: clamp((c.x2 - lb.padX) / lb.scale, 0, lb.srcW),
        y2: clamp((c.y2 - lb.padY) / lb.scale, 0, lb.srcH),
      },
      track_id: null,
      seen_for_seconds: null,
    }));
  }
}

interface Box {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  score: number;
  clsId: number;
}

/** Standard greedy class-aware NMS. */
function nms(boxes: Box[], iouThreshold: number): Box[] {
  // Sort highest-score first.
  const sorted = boxes.slice().sort((a, b) => b.score - a.score);
  const kept: Box[] = [];
  const suppressed = new Uint8Array(sorted.length);
  for (let i = 0; i < sorted.length; i++) {
    if (suppressed[i]) continue;
    const a = sorted[i]!;
    kept.push(a);
    for (let j = i + 1; j < sorted.length; j++) {
      if (suppressed[j]) continue;
      const b = sorted[j]!;
      if (a.clsId !== b.clsId) continue;
      if (iou(a, b) > iouThreshold) suppressed[j] = 1;
    }
  }
  return kept;
}

export function iou(a: { x1: number; y1: number; x2: number; y2: number }, b: typeof a): number {
  const interX1 = Math.max(a.x1, b.x1);
  const interY1 = Math.max(a.y1, b.y1);
  const interX2 = Math.min(a.x2, b.x2);
  const interY2 = Math.min(a.y2, b.y2);
  const interW = Math.max(0, interX2 - interX1);
  const interH = Math.max(0, interY2 - interY1);
  const inter = interW * interH;
  const areaA = Math.max(0, a.x2 - a.x1) * Math.max(0, a.y2 - a.y1);
  const areaB = Math.max(0, b.x2 - b.x1) * Math.max(0, b.y2 - b.y1);
  const union = areaA + areaB - inter;
  return union > 0 ? inter / union : 0;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
